<template>
  <div class="w-full" v-if="cuentosMostrados.length">
    <!-- Bloque de estadísticas con números más grandes -->
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
      <div class="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
        <h2 class="text-lg font-semibold text-gray-700 mb-2">Total de cuentos votados</h2>
        <div class="text-3xl font-bold text-gray-800">{{ totalCuentos }}</div>
      </div>
      <div class="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
        <h2 class="text-lg font-semibold text-gray-700 mb-2">Votación promedio</h2>
        <div class="text-3xl font-bold text-gray-800">{{ promedioVotacion.toFixed(1) }}</div>
      </div>
    </div>

    <!-- Bloque para el cuento baleado -->
    <div v-if="cuentoBaleado"
      class="flex items-center gap-2 bg-white p-4 rounded-lg border border-gray-200 mb-6 shadow-sm">
      <img src="https://www.onlygfx.com/wp-content/uploads/2021/10/bullet-9764.jpg" class="w-6 h-6 rounded-full"
        alt="Bala" />
      <p class="text-sm">
        Utilizaste la bala en el cuento
        <router-link :to="`/escuela/hernan-casciari/taller-2/votar?id=${cuentoBaleado.documentId}`"
          class="font-semibold text-blue-500 hover:underline">
          {{ cuentoBaleado.titulo }}
        </router-link>
      </p>
    </div>

    <!-- Tabla de cuentos votados -->
    <div class="overflow-x-auto bg-white rounded-lg border border-gray-200 shadow-sm">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th scope="col" class="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Título</th>
            <th scope="col" class="px-6 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Votos</th>
            <th scope="col" class="px-6 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Acciones</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="(cuento, index) in cuentosMostrados" :key="index"
            class="hover:bg-gray-50 cursor-pointer transition duration-150 ease-in-out"
            @click="visitarCuento(cuento.documentId)">
            <td class="px-6 py-1 whitespace-nowrap">
              <div class="flex items-center">
                <img v-if="cuento.bala" src="https://www.onlygfx.com/wp-content/uploads/2021/10/bullet-9764.jpg"
                  class="w-5 h-5 rounded-full mr-3" alt="Bala" />
                <div class="text-sm font-medium text-gray-900">{{ cuento.titulo }}</div>
              </div>
            </td>
            <td class="px-6 py-1 whitespace-nowrap">
              <div class="flex justify-center items-center">
                <span class="text-sm font-medium text-gray-900 mr-2">{{ cuento.votos }}</span>
                <div class="flex">
                  <svg v-for="star in 5" :key="star" xmlns="http://www.w3.org/2000/svg" fill="currentColor"
                    viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5" :class="{
                      'text-yellow-400': star <= cuento.votos,
                      'text-gray-300': star > cuento.votos,
                    }">
                    <path stroke-linecap="round" stroke-linejoin="round"
                      d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" />
                  </svg>
                </div>
              </div>
            </td>
            <td class="px-6 py-1 whitespace-nowrap text-center">
              <button @click.stop="visitarCuento(cuento.documentId)" class="text-blue-600 hover:text-blue-900">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                  stroke="currentColor" class="w-6 h-6">
                  <path stroke-linecap="round" stroke-linejoin="round"
                    d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Paginación -->
    <div v-if="totalPaginas > 1" class="flex justify-between items-center mt-6">
      <button @click="prevPage" :disabled="paginaActual === 1"
        class="p-2 text-blue-600 disabled:text-gray-400 hover:text-blue-800 transition duration-150 ease-in-out">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"
          class="w-5 h-5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
        </svg>
      </button>
      <span class="text-sm font-medium text-gray-700">Página {{ paginaActual }} de {{ totalPaginas }}</span>
      <button @click="nextPage" :disabled="paginaActual === totalPaginas"
        class="p-2 text-blue-600 disabled:text-gray-400 hover:text-blue-800 transition duration-150 ease-in-out">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"
          class="w-5 h-5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
        </svg>
      </button>
    </div>
  </div>
  <!-- Mensaje para ir a votar si no hay cuentos votados -->
  <div v-else class="text-center mt-8">
    <p class="text-gray-600 mb-2">
      No votaste ningún cuento aún. ¿Qué estás esperando?
    </p>
    <router-link to="/virtuales/hernan-casciari/taller-2/votar"
      class="text-blue-600 hover:text-blue-800 font-medium">¡Vota ahora!</router-link>
  </div>
</template>

<script>
import { useAuthStore } from "@/stores/auth";
import { useRouter } from 'vue-router';

const SERVER = "https://staging-strapi.orsai.org";

export default {
  setup() {
    const router = useRouter();
    return { router };
  },
  data() {
    return {
      cuentos: [],
      cuentosMostrados: [],
      totalCuentos: 0,
      paginaActual: 1,
      limitePorPagina: 10,
      totalPaginas: 1,
      cuentoBaleado: null,
      promedioVotacion: 0,
    };
  },
  computed: {
    computedUserId() {
      const authStore = useAuthStore();
      return authStore.usuario?.id || 1384;
    },
  },
  methods: {
    async fetchCuentos() {
      try {
        if (!this.computedUserId) {
          console.error("No se pudo obtener el ID del usuario.");
          return;
        }

        const response = await fetch(
          `${SERVER}/api/cuentos/voted-by-user/${this.computedUserId}`
        );
        const data = await response.json();

        console.log("Cuentos votados por el usuario:", data);

        this.cuentos = data.data;
        this.totalCuentos = data.meta.total;
        this.totalPaginas = Math.ceil(this.totalCuentos / this.limitePorPagina);

        this.cuentoBaleado = this.cuentos.find((cuento) => cuento.bala);
        this.actualizarCuentosMostrados();

        // Calcular el promedio de votación
        const sumaVotos = this.cuentos.reduce((sum, cuento) => sum + cuento.votos, 0);
        this.promedioVotacion = this.totalCuentos > 0 ? sumaVotos / this.totalCuentos : 0;
      } catch (error) {
        console.error("Error al cargar los cuentos:", error);
      }
    },
    prevPage() {
      if (this.paginaActual > 1) {
        this.paginaActual--;
        this.actualizarCuentosMostrados();
      }
    },
    nextPage() {
      if (this.paginaActual < this.totalPaginas) {
        this.paginaActual++;
        this.actualizarCuentosMostrados();
      }
    },
    actualizarCuentosMostrados() {
      const inicio = (this.paginaActual - 1) * this.limitePorPagina;
      const fin = inicio + this.limitePorPagina;
      this.cuentosMostrados = this.cuentos.filter(cuento => !cuento.bala).slice(inicio, fin);
    },
    visitarCuento(documentId) {
      this.router.push(`/escuela/hernan-casciari/taller-2/votar?id=${documentId}`);
    },
  },
  mounted() {
    this.fetchCuentos();
  },
};
</script>

<style scoped>
@media (max-width: 640px) {
  .overflow-x-auto {
    -webkit-overflow-scrolling: touch;
  }
}
</style>